/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../core/components';
import { Component, ElementRef, QueryList, ViewChild, ViewChildren } from '@angular/core';
import {
    DragDropService,
    LanguageService,
    LoadingService,
    LogService,
    NavigationService,
    UtilsService
} from '../../../../../../core/services';
import { DropdownModel, ErrorResponseModel } from '../../../../../../core/models';
import {
    GslbApiService,
    GslbBackendModel,
    GslbFrontendModel,
    GslbRealServiceModel,
    HealthChecksApiService,
    LbErrorService
} from '../../../../shared';
import { forkJoin, Observable } from 'rxjs';
import { hostnameValidation, nameValidation, portRangeValidation } from '../../../../../../core/directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-add-edit-pools',
    templateUrl: './addEditPools.component.html',
    styleUrls: ['./addEditPools.component.scss']
})

export class AddEditPoolsComponent extends BaseComponent {
    /**
     * Monitor codes dom reference
     */
    @ViewChildren('codes')
    public codes: QueryList<any> = new QueryList<any>();

    /**
     * Add monitor code dom reference.
     */
    @ViewChild('addCodeButton', { static: true })
    public addCodeButton: ElementRef;

    /**
     * Store the dropdown content for use in the view.
     */
    public dropdown: any = {
        backEndMonitor: [],
        backEndMonitorRetries: [],
        backendMonitorStatus: [],
        backendLbMethod: [],
        backendFallback: [],
        healthCheckScripts: []
    };

    /**
     * Store the id of the pool we're editing.
     */
    public editId: string;

    /**
     * Pool form group.
     */
    public poolForm: FormGroup;

    /**
     * Our available made pools from the API.
     */
    public pools: GslbBackendModel[] = [];

    /**
     * Our available made members from the API.
     */
    public members: GslbRealServiceModel[] = [];

    /**
     * Our available made global names from the API.
     */
    public globalNames: GslbFrontendModel[] = [];

    /**
     * Available members in the drag and drop table.
     */
    public availableMembers: any = [];

    /**
     * Selected members in the drag and drop table.
     */
    public selectedMembers: any = [];

    /**
     * Store the state of the drag.
     */
    public dragActive: boolean = false;

    /**
     * Flag for data availability.
     */
    public dataAvailable: boolean = false;

    /**
     * Store the list of healthcheck scripts.
     */
    public healthCheckScripts: any[];

    /**
     * Store the data for the pool we are editing.
     * @private
     */
    private currentPool: any;

    /**
     * Store initial data for comparison.
     * @private
     */
    private initialData: any = {};

    /**
     * Private variable for monitor code getter/setter.
     * @private
     */
    private _monitorCodes: FormArray;

    constructor(
        private dragDropService: DragDropService,
        private formBuilder: FormBuilder,
        private gslbApiService: GslbApiService,
        private healthChecksApiService: HealthChecksApiService,
        private languageService: LanguageService,
        private lbErrorService: LbErrorService,
        private loadingService: LoadingService,
        private logService: LogService,
        private navigationService: NavigationService,
        private route: ActivatedRoute,
        private router: Router,
        private utilsService: UtilsService,
    ) {
        super();

        this.setupForm();
        this.setupFormSubscribers();
        this.setupPage();
        this.setupDragNDropService();
    }

    /**
     * Getter for global names form array.
     */
    get globalNameControls(): FormArray {
        return this.poolForm.get('globalNamesIDs') as FormArray;
    }

    /**
     * Getter for expected monitor codes in the form.
     */
    get monitorCodes(): FormArray {
        this._monitorCodes = this.poolForm.get('monitorExpectedCodes') as FormArray;

        return this._monitorCodes;
    }

    /**
     * Returns form control as FormControl.
     * @param control
     */
    public getFormControl(control: any): FormControl {
        return control as FormControl;
    }

    /**
     * Add a monitor code row to the form.
     */
    public addMonitorCode(): void {
        this.monitorCodes.push(new FormControl('', [Validators.required]));
        this.focusNewMonitorCodeInput();
    }

    /**
     * Remove monitor code field from form.
     * @param index
     */
    public removeMonitorCode(index: number): void {
        this.monitorCodes.removeAt(index);
        this.focusNewMonitorCodeInput();
        this.validateMonitorCodes();
    }

    /**
     * Validate monitor codes.
     */
    public validateMonitorCodes(): void {
        const duplicates: any[] = [];

        for (const control of this.monitorCodes.controls) {
            if (duplicates.includes(control.value)) {
                control.setErrors({
                    'duplicateValidator':
                        `${this.languageService.translate('code')}
                        ${this.languageService.translate('already_in_use')}`
                });
                control.markAsTouched();
            } else {
                if (control.hasError('duplicateValidator')) {
                    control.setErrors({ 'duplicateValidator': null });
                    control.updateValueAndValidity();
                }
            }
            duplicates.push(control.value);
        }
    }

    /**
     * Get member name by id.
     * @param id
     */
    public getMemberName(id: string): string {
        return this.members.find(item => item.id === id).name;
    }

    /**
     * Helper function to get the selected members count.
     */
    public getSelectedMemberLength(): number {
        if (this.members !== undefined) {
            return this.poolForm.get('membersIDs')?.value?.length || 0;
        } else {
            return 0;
        }
    }

    /**
     * Monitor changes in the pool form.
     */
    public monitorChange(): void {
        this.disablePoolsFormControls();

        switch (this.poolForm.get('monitor').value) {
            case 'http':
                this.poolForm.get('monitorUseSSL').enable();
                this.poolForm.get('monitorURLPath').enable();
                this.poolForm.get('monitorHostname').enable();
                this.poolForm.get('monitorPort').enable();
                this.poolForm.get('monitorExpectedCodes').enable();
                break;
            case 'tcp':
                this.poolForm.get('monitorPort').enable();
                this.poolForm.get('monitorSendString').enable();
                this.poolForm.get('monitorMatchRe').enable();
                break;
            case 'forced':
                this.poolForm.get('monitorStatus').enable();
                break;
            case 'external':
            case 'external_weight':
                this.poolForm.get('monitorExternalScriptID').enable();
        }
    }

    /**
     * Update form with the selected members.
     * @param event
     */
    public updateSelected(event: any): void {
        this.poolForm.get('membersIDs').setValue(event);
    }

    /**
     * Save the pool.
     */
    public submitPool(): Observable<any> {
        return new Observable((observer) => {
            const apiCall = this.getApiCall();
            apiCall
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (data: any): void => {
                        this.navigateToURL('/cluster-configuration/gslb?sFilter=pools');
                        observer.next(data);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.lbErrorService.pageWithErrors(this.poolForm, error.error.errors);
                        this.logService.error('GslbComponent: submitPool', 'Error submitting GSLB data', error);
                        this.loadingService.loadingButtonEmitter.next(false);
                        observer.error(error);
                        observer.complete();
                    }
                })
                .add((): void => {
                    observer.next();
                    observer.complete();
                });
        });
    }

    /**
     * Focus cursor to the last/new monitor code field on the form.
     * @private
     */
    private focusNewMonitorCodeInput(): void {
        const timerId = setTimeout((): void => {
            this.codes.last.nativeElement.focus();
            clearTimeout(timerId);
        }, 50);
    }

    /**
     * Disabled pools form controls.
     * @private
     */
    private disablePoolsFormControls(): void {
        this.poolForm.get('monitorUseSSL').disable();
        this.poolForm.get('monitorURLPath').disable();
        this.poolForm.get('monitorHostname').disable();
        this.poolForm.get('monitorPort').disable();
        this.poolForm.get('monitorExpectedCodes').disable();
        this.poolForm.get('monitorSendString').disable();
        this.poolForm.get('monitorMatchRe').disable();
        this.poolForm.get('monitorStatus').disable();
        this.poolForm.get('monitorExternalScriptID').disable();
    }

    /**
     * Get the api call based on mode.
     * @private
     */
    private getApiCall(): any {
        const pool = this.poolForm.value;
        pool.globalNamesIDs = this.getGlobalNameIds(pool.globalNamesIDs);

        return this.editId
            ? this.gslbApiService.updateBackend(this.currentPool.id, pool)
            : this.gslbApiService.addBackend(pool);
    }

    /**
     * Get global name Ids.
     * @param globalNameCheckboxes
     * @private
     */
    private getGlobalNameIds(globalNameCheckboxes: boolean[]): string[] {
        const globalNameIds: any[] = [];
        let index: number = 0;
        for (const checkboxValue of globalNameCheckboxes) {
            if (checkboxValue) {
                globalNameIds.push(this.globalNames[index].id);
            }
            index++;
        }

        return globalNameIds;
    }

    /**
     * Set the pools resolver data.
     * @private
     */
    private setupPage(): void {
        this.editId = this.route.snapshot.params['id'];

        forkJoin({
            hcScripts: this.healthChecksApiService.getAllHealthChecks(),
            globalNames: this.gslbApiService.getFrontends(),
            members: this.gslbApiService.getRealServers(),
            pools: this.gslbApiService.getBackends()
        })
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroup: any): void => {
                    if (dataGroup['hcScripts']?.length) {
                        this.healthCheckScripts = this.filterCompatibleHealthcheckScripts(dataGroup['hcScripts']);
                    }

                    if (dataGroup['globalNames']?.length) {
                        this.globalNames = dataGroup['globalNames'];
                        this.getGlobalNamesControls();
                    }

                    if (dataGroup['members']?.length) {
                        this.members = dataGroup['members'];
                        this.setAvailableMembers();
                    }

                    if (dataGroup['pools']?.length) {
                        this.pools = dataGroup['pools'];

                        if (this.editId) {
                            this.currentPool = this.pools.find((item: GslbBackendModel): boolean => item.id === this.editId);
                            this.patchDataToForm(this.currentPool);
                        }
                    }

                    this.setUpPoolsDropdownContent();
                    this.dataAvailable = true;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'AddEditPoolsComponent: getDataForPage',
                        'Failed to get data for page.',
                        error
                    );
                }
            });
    }

    /**
     * Filter the list of healthcheck scripts that are GSLB compatible.
     * @param hcScripts
     * @private
     */
    private filterCompatibleHealthcheckScripts(hcScripts: any[]): any[] {
        return hcScripts.filter(item => item.services.includes('gslb'));
    }

    /**
     * Set the members data for the drag and drop.
     * @private
     */
    private setAvailableMembers(): void {
        if (this.members) {
            for (const member of this.members) {
                this.availableMembers.push({ id: member.id, name: member.name });
            }
        }
        const timerId = setTimeout((): void => {
            this.dragDropService.reRunInitialSetup.next(true);
            clearTimeout(timerId);
        }, 0);
    }

    /**
     * Get the global name controls.
     * @private
     */
    private getGlobalNamesControls(): void {
        if (this.globalNames !== undefined) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            for (const globalName of this.globalNames) {
                this.globalNameControls.push(new FormControl(null));
            }
        }
    }

    /**
     * Setup dragNdrop service.
     * @private
     */
    private setupDragNDropService(): void {
        this.dragDropService.dragActive
            .pipe(takeUntil(this.destroyed$))
            .subscribe((state: boolean): void => {
                this.dragActive = state;
            });
    }

    /**
     * Set up the formGroup.
     * @private
     */
    private setupForm(): void {
        this.poolForm = this.formBuilder.group({
            name: [null, [Validators.required, nameValidation]],
            monitor: ['http', []],
            monitorExternalScriptID: [{ value: null, disabled: true }],
            monitorInterval: [1, [Validators.min(1), Validators.max(3600)]],
            monitorTimeout: [100, [Validators.min(100), Validators.max(10000)]],
            monitorRetries: [1],
            monitorUseSSL: [false],
            monitorURLPath: ['/', Validators.required],
            monitorHostname: ['', hostnameValidation],
            monitorPort: [80, [Validators.required, portRangeValidation]],
            monitorExpectedCodes: new FormArray([new FormControl('', [Validators.required])]),
            monitorSendString: [''],
            monitorMatchRe: [''],
            monitorStatus: ['up'],
            lbMethod: ['wrr'],
            fallback: ['any'],
            maxAddrsReturned: [1, [Validators.min(1), Validators.max(1024)]],
            globalNamesIDs: new FormArray([]),
            membersIDs: [[]]
        });
    }

    /**
     * Setup form subscribers.
     * @private
     */
    private setupFormSubscribers(): void {
        this.poolForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: form => {
                    this.utilsService.debounce('form', (): void => {
                        this.validatePoolName(form['name']);

                        if (this.editId
                            && !this.utilsService.hasFormDataChanged(this.initialData, form)) {
                            this.poolForm.markAsPristine();
                        } else {
                            this.poolForm.markAsDirty();
                        }
                    }, 250);
                }
            });

        this.poolForm.get('monitorExpectedCodes').valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.utilsService.debounce('codes', (): void => {
                        this.validateMonitorCodes();
                    }, 250);
                }
            });
    }

    /**
     * Validate pool name.
     * @param name
     * @private
     */
    private validatePoolName(name: string): void {
        const poolName: AbstractControl = this.poolForm.get('name');
        let duplicatePool: GslbBackendModel = this.pools.find((pool: GslbBackendModel): boolean => pool.name === name);

        if (this.editId && duplicatePool?.id === this.currentPool.id) {
            duplicatePool = undefined;
        }

        if (duplicatePool) {
            poolName.setErrors({ 'duplicateName': `${this.languageService.translate('duplicate_pool')}` });
        }
    }

    /**
     * Set the pool arrays.
     * @private
     */
    private setUpPoolsDropdownContent(): void {
        this.dropdown.backEndMonitor = [
            <DropdownModel>{ key: 'http', value: 'http' },
            <DropdownModel>{ key: 'tcp', value: 'tcp' },
            <DropdownModel>{ key: 'external', value: 'External' },
            <DropdownModel>{ key: 'external_weight', value: 'External Dynamic Weight' },
            <DropdownModel>{ key: 'forced', value: 'forced' },
        ];

        const retries: number[] = [0, 1, 2, 3, 4, 5];
        this.dropdown.backEndMonitorRetries = this.utilsService.convertArrayToDropDown(retries);

        this.dropdown.backendMonitorStatus = [
            <DropdownModel>{ key: 'up', value: 'up' },
            <DropdownModel>{ key: 'down', value: 'down' },
        ];

        this.dropdown.backendLbMethod = [
            <DropdownModel>{ key: 'wrr', value: 'Weighted round robin (wrr)' },
            <DropdownModel>{ key: 'twrr', value: 'Topology weighted round robin (twrr)' },
            <DropdownModel>{ key: 'fogroup', value: 'Failover Group (fogroup)' },
        ];

        this.dropdown.backendFallback = [
            <DropdownModel>{ key: 'any', value: 'any' },
            <DropdownModel>{ key: 'refuse', value: 'refuse' },
        ];

        this.dropdown.healthCheckScripts = this.getUpHCScriptsDropdown();
    }

    /**
     * Get healthcheck scripts dropdown data.
     * @private
     */
    private getUpHCScriptsDropdown(): DropdownModel[] {
        const scripts: DropdownModel[] = [];
        for (const script of this.healthCheckScripts) {
            scripts.push(<DropdownModel>{ key: script.id, value: script.name });
        }

        return scripts;
    }

    /**
     * Patch form values.
     * @param data
     * @private
     */
    private patchDataToForm(data: any): void {
        this.setupSelectedMembers(data.membersIDs);
        // Create an id for each of the global name to match with their corresponding checkboxes.
        data.globalNamesIDs = this.globalNames.map((name: GslbFrontendModel) => {
            return !!data.globalNamesIDs.includes(name.id);
        });
        this.poolForm.patchValue(data);
        this.initialData = this.poolForm.value;
    }

    /**
     * Set the drag N drop selected members.
     * @param memberIds
     * @private
     */
    private setupSelectedMembers(memberIds: string[]): void {
        this.selectedMembers = memberIds;
        for (const id of memberIds) {
            const availableIndex = this.availableMembers.findIndex((member: any): boolean => member.id === id);
            if (availableIndex !== -1) {
                this.availableMembers.splice(availableIndex, 1);
            }
        }
    }

    /**
     * Navigate to supplied URL.
     * @param url
     * @private
     */
    private navigateToURL(url: string): void {
        this.router.navigateByUrl(this.navigationService.getEnduranceUrl(url)).then().catch();
    }
}
